import Image from 'next/image'
import {FC} from 'react'

import {useHomepageContent} from '@/content/homepage'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Section} from '../common/content/Section'
import {Text, TextVariant} from '../common/content/Text'
import {TrademarkBenefitsCarousel} from './TrademarkBenefitsCarousel'

export type TrademarkImportanceBenefit = {
  title: string
  description: string
  imageSrc: string
  imageAlt: string
}

type TrademarkImportanceProps = {
  title?: string
  benefits: TrademarkImportanceBenefit[]
}

export const TrademarkImportance: FC<React.PropsWithChildren<TrademarkImportanceProps>> = ({
  title,
  benefits,
}) => {
  const {t} = useTranslation()
  const {whyRegisterTm} = useHomepageContent()

  return (
    <>
      <Section hasPaddingTop isHighlighted>
        <Text variant={TextVariant.h2} align="center">
          {title || t(whyRegisterTm.title)}
        </Text>
        <ul className="list">
          {benefits.map(({title, description, imageSrc, imageAlt}) => (
            <li key={title} className="item">
              <div className="image-wrapper">
                <Image
                  src={imageSrc}
                  alt={imageAlt}
                  style={{objectFit: 'contain'}}
                  fill
                  sizes={`${MOBILE_SCREENSIZE} 50px, 80px`}
                />
              </div>
              <div className="content">
                <Text variant={TextVariant.h4} element="h3">
                  {t(title)}
                </Text>
                <div className="description">
                  <Text color={theme.palette.text.light}>{t(description)}</Text>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <TrademarkBenefitsCarousel />
      </Section>

      <style jsx>{`
        .list {
          display: flex;
          justify-content: space-between;
          margin-top: ${theme.spacing[8]};
        }
        .item {
          display: flex;
          align-items: center;
          flex: 1;
        }
        .item + .item {
          margin-left: ${theme.spacing[8]};
        }
        .image-wrapper {
          position: relative;
          width: 80px;
          height: 105px;
          margin-right: ${theme.spacing[4]};
        }
        .content {
          flex: 1;
        }
        .description {
          margin-top: ${theme.spacing[2]};
        }
        @media ${TABLET_SCREENSIZE} {
          .list {
            flex-direction: column;
            max-width: 500px;
            margin-top: ${theme.spacing[7]};
            margin-left: auto;
            margin-right: auto;
          }
          .item {
            flex-direction: column;
          }
          .item + .item {
            margin-left: 0;
            margin-top: ${theme.spacing[6]};
          }
          .image-wrapper {
            height: 90px;
            margin-right: 0;
            margin-bottom: ${theme.spacing[3]};
          }
          .content {
            text-align: center;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            margin-top: ${theme.spacing[6]};
          }
          .item + .item {
            margin-top: ${theme.spacing[5]};
          }
          .image-wrapper {
            height: 64px;
          }
        }
      `}</style>
    </>
  )
}
